import {useCallback, useMemo, useState} from 'react'
import {useLogsQuery} from './useLogs'
import {usePendingPrompt} from './use-pending-prompt'
import type {LogEntry, SessionState} from '../types/session'
import type {NormalizedPrompt} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import {extractPromptFromEntry} from '../components/session-logs/prompt-log-utils'
import {useSessionCommandsQuery} from './useListCommands'
import {getPromptResponseResolvedPromptIds} from '../utils/permission-prompt-helpers'

export interface UseCLIPromptStateOptions {
  /** The session ID to monitor for prompts */
  sessionId: string | undefined
  /** Whether to enable the hook (typically: isCLI && !!sessionId) */
  enabled: boolean
  /** Current session state. */
  sessionState?: SessionState
}

export interface CLIPromptState {
  /** The current pending CLI prompt, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** Zero-based index of the current pending prompt. */
  pendingPromptIndex: number
  /** Total number of unresolved prompts. */
  pendingPromptCount: number
  /** Whether logs are still loading */
  isLoading: boolean
  /**
   * True when the pending prompt is older than the resume window AND the
   * session is paused (`waiting_for_user`, i.e. CCA only).
   */
  isExpired: boolean
  /** Raw session log entries the hook fetched. May be `undefined` while logs are loading. */
  logs: LogEntry[] | undefined
  /**
   * Optimistically mark a prompt resolved.
   * Pair with {@link unmarkPromptResolved} on mutation failure to restore the prompt.
   */
  markPromptResolved: (toolCallId: string) => void
  /**
   * Roll back an optimistic resolution when the underlying mutation
   * fails so the prompt re-appears for the user to retry.
   */
  unmarkPromptResolved: (toolCallId: string) => void
  /** Dismiss the current prompt without marking it as resolved */
  dismissPrompt: () => void
  /** Select the previous unresolved prompt, if one exists */
  goToPreviousPrompt: () => void
  /** Select the next unresolved prompt, if one exists */
  goToNextPrompt: () => void
}

/**
 * Hook that monitors a CLI session for pending prompts.
 */
export function useCLIPromptState({sessionId, enabled, sessionState}: UseCLIPromptStateOptions): CLIPromptState {
  const [resolvedPromptIds, setResolvedPromptIds] = useState<Set<string>>(() => new Set())

  const {data: logs, isLoading: isLogsLoading} = useLogsQuery({
    sessionId,
    enabled: enabled && !!sessionId,
  })

  const {data: commandsData, isLoading: isCommandsLoading} = useSessionCommandsQuery({
    sessionId,
    enabled: enabled && !!sessionId,
  })

  const prompts = useMemo(() => {
    if (!logs?.length) return []
    return logs.flatMap(entry => {
      const prompt = extractPromptFromEntry(entry)
      return prompt ? [prompt] : []
    })
  }, [logs])

  const resolvedIds = useMemo(() => {
    const ids = new Set(resolvedPromptIds)
    for (const id of getPromptResponseResolvedPromptIds(commandsData?.commands, prompts)) {
      ids.add(id)
    }
    return ids
  }, [commandsData?.commands, prompts, resolvedPromptIds])

  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const {
    pendingPrompt: firstPendingPrompt,
    pendingPrompts,
    isExpired,
  } = usePendingPrompt(logs, resolvedIds, sessionState)
  const isLoading = isLogsLoading || isCommandsLoading

  const selectedPromptIndex = useMemo(() => {
    if (!pendingPrompts.length) return -1
    if (!selectedPromptId) return 0
    const index = pendingPrompts.findIndex(prompt => prompt.toolCallId === selectedPromptId)
    return index === -1 ? 0 : index
  }, [pendingPrompts, selectedPromptId])

  const visiblePendingPrompt = isLoading ? null : (pendingPrompts[selectedPromptIndex] ?? firstPendingPrompt)

  const markPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.add(toolCallId)
      return next
    })
  }, [])

  const unmarkPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (!prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.delete(toolCallId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (visiblePendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(visiblePendingPrompt.toolCallId))
    }
  }, [visiblePendingPrompt])

  const goToPreviousPrompt = useCallback(() => {
    setSelectedPromptId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(prompt => prompt.toolCallId === prev) : selectedPromptIndex
      const nextIndex = Math.max(0, currentIndex - 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedPromptIndex])

  const goToNextPrompt = useCallback(() => {
    setSelectedPromptId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(prompt => prompt.toolCallId === prev) : selectedPromptIndex
      const nextIndex = Math.min(pendingPrompts.length - 1, currentIndex + 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedPromptIndex])

  return {
    pendingPrompt: visiblePendingPrompt,
    pendingPromptIndex: visiblePendingPrompt ? selectedPromptIndex : -1,
    pendingPromptCount: pendingPrompts.length,
    isLoading,
    isExpired,
    logs,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
  }
}
